import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/home.css";
import "../styles/profile.css";
import BottomNav from "../components/BottomNav";
import Navbar from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

const Profile = () => {
  const { userProfile, logout, currentUser } = useAuth();
  const { cartItems } = useCart();

  const [orders, setOrders] = useState([]);
  const [delivered, setDelivered] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const navigate = useNavigate();

  // Fetch Orders from Firestore
  useEffect(() => {
    const fetchOrders = async () => {
      if (!currentUser) return;

      try {
        const q = query(
          collection(db, "orders"),
          where("userId", "==", currentUser.uid)
        );

        const querySnapshot = await getDocs(q);

        const ordersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setOrders(ordersData);
        setDelivered(ordersData.filter((order) => order.status === "delivered").length);
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };

    fetchOrders();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Failed to logout");
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (!userProfile) {
    return (
      <div className="profile-container">
        <Navbar />
        <div className="loading">Loading profile...</div>
        <BottomNav />
      </div>
    );
  }

  const displayName =
    userProfile.displayName ||
    `${userProfile.firstName} ${userProfile.lastName}`;

  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  return (
    <div className="profile-container">
      <Navbar />

      <div className="profile-card">
        <div className="profile-img-container">
          <div className="profile-img-initials">{initials}</div>
        </div>

        <h2>{displayName}</h2>
        <p className="profile-email">{userProfile.email}</p>

        <div className="profile-details">
          <div className="detail-row">
            <span className="detail-label">First Name:</span>
            <span className="detail-value">{userProfile.firstName}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Last Name:</span>
            <span className="detail-value">{userProfile.lastName}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Member Since:</span>
            <span className="detail-value">
              {new Date(userProfile.createdAt).toLocaleDateString()}
            </span>
          </div>

         

        <div className="profile-info">
          <div className="info-box">
            <h4>Orders</h4>
            <p>{orders.length}</p>
          </div>

          <div className="info-box">
            <h4>Delivered</h4>
            <p>{delivered}</p>
          </div>

          <div className="info-box">
            <h4>Cart</h4>
            <p>{cartItems.length}</p>
          </div>
        </div>

        <div className="profile-buttons">
          <button
            className="edit-btn"
            onClick={() => navigate("/edit-profile")}
          >
            Edit Profile
          </button>

          <button
            className="logout-btn"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? "Logging out..." : "Logout"}
          </button>
        </div>

        <div className="order-details">
            <h3>Order History</h3>
            {orders.length > 0 ? (
              <div className="orders-container">
                {orders.map((order) => {
                  const isCompleted = order.status === 'delivered' || order.status === 'cancelled';
                  const product = order.items && order.items.length > 0 ? order.items[0] : null;

                  // Calculate expected delivery date (5-6 days after order date)
                  const calculateExpectedDelivery = (orderDate) => {
                    if (!orderDate) return null;
                    const orderDateObj = new Date(orderDate);
                    const expectedDate = new Date(orderDateObj);
                    expectedDate.setDate(orderDateObj.getDate() + 6); // 6 days for 5-6 day range
                    return expectedDate;
                  };

                  const expectedDeliveryDate = calculateExpectedDelivery(order.createdAt);

                  return (
                    <div
                      key={order.id}
                      className={`order-card ${isCompleted ? 'completed' : 'active'}`}
                    >
                      <div className="order-card-content">
                        <div className="order-card-info">
                          <div className="order-main-info">
                            <p className="order-product-name">
                              {product ? product.name : 'Product name not available'}
                            </p>
                            <p className="order-price">
                              ₹{product ? product.price : 'N/A'}
                            </p>
                            <div className="status-badge" data-status={order.status}>
                              {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Unknown'}
                            </div>
                            {expectedDeliveryDate && !isCompleted && (
                              <p className="expected-delivery">
                                <strong>Expected Delivery:</strong> {expectedDeliveryDate.toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {isCompleted && (
                        <div className="completed-overlay">
                          <span className="completed-text">
                            {order.status === 'delivered' ? '✓ Completed' : '✗ Cancelled'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="no-orders">No orders found.</p>
            )}
          </div>
        </div>

      </div>

      <BottomNav />
    </div>
  );
};

export default Profile;